import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Database, Download, Trash2, Upload, AlertTriangle } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const DataManagementSection = () => {
  const [localStorageEnabled, setLocalStorageEnabled] = useState(true);
  const [autoDeleteEnabled, setAutoDeleteEnabled] = useState(false);
  const [retentionPeriod, setRetentionPeriod] = useState("90");
  const [backupFrequency, setBackupFrequency] = useState("never");
  const [loading, setLoading] = useState(false);

  const handleLocalStorageToggle = async (enabled: boolean) => {
    setLoading(true);
    try {
      // TODO: Implement local storage toggle logic
      setLocalStorageEnabled(enabled);
      toast({
        title: enabled ? "Local Storage Enabled" : "Local Storage Disabled",
        description: enabled 
          ? "Messages will now be stored locally on your device" 
          : "Messages will be stored on our servers",
      });
    } catch (error) {
      console.error('Error toggling local storage:', error);
      toast({
        title: "Error",
        description: "Failed to update storage settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackupMessages = async () => {
    setLoading(true);
    try {
      // TODO: Implement message backup logic
      toast({
        title: "Backup Started",
        description: "Your messages are being backed up to the server",
      });
    } catch (error) {
      console.error('Error backing up messages:', error);
      toast({
        title: "Backup Failed",
        description: "Unable to backup messages. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearLocalMessages = async () => {
    setLoading(true);
    try {
      // TODO: Implement local message clearing logic
      toast({
        title: "Local Messages Cleared",
        description: "All messages have been removed from this device",
      });
    } catch (error) {
      console.error('Error clearing local messages:', error);
      toast({
        title: "Error",
        description: "Failed to clear local messages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteServerBackup = async () => {
    setLoading(true);
    try {
      // TODO: Implement server backup deletion logic
      toast({
        title: "Server Backup Deleted",
        description: "Your message backup has been permanently removed from our servers",
      });
    } catch (error) {
      console.error('Error deleting server backup:', error);
      toast({
        title: "Error",
        description: "Failed to delete server backup",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      // TODO: Implement data export logic
      toast({
        title: "Export Started",
        description: "Your data export will be ready for download shortly",
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: "Export Failed",
        description: "Unable to export your data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteDataDeletion = async () => {
    setLoading(true);
    try {
      // TODO: Implement complete data deletion logic
      toast({
        title: "Account Deletion Initiated",
        description: "Your account and all data will be permanently deleted within 24 hours",
        variant: "destructive",
      });
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: "Deletion Failed",
        description: "Unable to delete your account. Please contact support.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-gray-900/80 border-gray-600">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-ice-white">
          <Database className="w-5 h-5 text-pink-500" />
          Data Management
        </CardTitle>
        <CardDescription className="text-gray-300">
          Control your data storage, backup, and deletion preferences
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Messages Storage Control */}
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-ice-white">Message Storage</h4>
          
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="local-storage">Store messages locally only</Label>
              <p className="text-sm text-gray-500">Keep messages on your device instead of our servers</p>
            </div>
            <Switch 
              id="local-storage" 
              checked={localStorageEnabled}
              onCheckedChange={handleLocalStorageToggle}
              disabled={loading}
            />
          </div>

          {localStorageEnabled && (
            <div className="space-y-3 ml-4 border-l-2 border-gray-700 pl-4">
              <div className="space-y-2">
                <Label htmlFor="backup-frequency">Backup frequency</Label>
                <Select value={backupFrequency} onValueChange={setBackupFrequency}>
                  <SelectTrigger className="bg-carbon border-ice-white/20 text-ice-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-carbon border-ice-white/20">
                    <SelectItem value="never">Never</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handleBackupMessages}
                disabled={loading}
              >
                <Upload className="w-4 h-4 mr-2" />
                Backup Messages to Server Now
              </Button>
            </div>
          )}
        </div>

        {/* Message Deletion Controls */}
        <div className="space-y-4 border-t border-gray-700 pt-4">
          <h4 className="text-lg font-medium text-ice-white">Message Deletion</h4>
          
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="auto-delete">Auto-delete messages after blocking user</Label>
              <p className="text-sm text-gray-500">Automatically remove conversation when blocking someone</p>
            </div>
            <Switch 
              id="auto-delete" 
              checked={autoDeleteEnabled}
              onCheckedChange={setAutoDeleteEnabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="retention-period">Message retention period</Label>
            <Select value={retentionPeriod} onValueChange={setRetentionPeriod}>
              <SelectTrigger className="bg-carbon border-ice-white/20 text-ice-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-carbon border-ice-white/20">
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
                <SelectItem value="never">Never delete</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Local Message History
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-carbon border-ice-white/20">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-ice-white">Clear Local Messages?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-300">
                    This will permanently delete all messages stored on this device. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-gray-700 text-ice-white border-gray-600">Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleClearLocalMessages}
                  >
                    Clear Messages
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Server Message Backup
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-carbon border-ice-white/20">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-ice-white">Delete Server Backup?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-300">
                    This will permanently delete your message backup from our servers. You won't be able to recover these messages on other devices.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-gray-700 text-ice-white border-gray-600">Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleDeleteServerBackup}
                  >
                    Delete Backup
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Data Export */}
        <div className="space-y-4 border-t border-gray-700 pt-4">
          <h4 className="text-lg font-medium text-ice-white">Data Export</h4>
          
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={handleExportData}
            disabled={loading}
          >
            <Download className="w-4 h-4 mr-2" />
            Export All My Data (GDPR Compliant)
          </Button>
        </div>

        {/* Nuclear Option - Complete Data Deletion */}
        <div className="space-y-4 border-t border-red-900/50 pt-4 bg-red-950/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h4 className="text-lg font-medium text-red-400">Danger Zone</h4>
          </div>
          
          <p className="text-sm text-gray-300">
            Permanently delete your Alpha account and all associated data. This action is irreversible.
          </p>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="destructive" 
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete All My Data from Alpha
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-carbon border-red-600/50">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Permanently Delete Account?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-gray-300">
                  <div className="space-y-2">
                    <p>This will permanently delete:</p>
                    <ul className="list-disc ml-6 space-y-1">
                      <li>Your profile and all personal information</li>
                      <li>All messages and conversations</li>
                      <li>Your matches and interactions</li>
                      <li>Your photos and media</li>
                      <li>Your subscription and payment history</li>
                      <li>All other data associated with your account</li>
                    </ul>
                    <p className="font-medium text-red-400 mt-4">
                      This action cannot be undone. You will lose access to your account immediately.
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-gray-700 text-ice-white border-gray-600">Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleCompleteDataDeletion}
                >
                  Yes, Delete Everything
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};

export default DataManagementSection;